import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Box,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const VerifyAccountDeletion: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Guards against the effect firing twice (e.g. React.StrictMode, dependency
  // recreation in useAuth). One-shot verification — token can only be used once.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const verifyDeletion = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      try {
        await api.post('/users/account/verify-deletion', { token });
        setSuccess(true);
        // Logout after 3 seconds
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 3000);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to verify account deletion'
        );
      } finally {
        setLoading(false);
      }
    };

    verifyDeletion();
  }, [searchParams, logout, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Account Deletion
        </Typography>

        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Processing your request...</Typography>
          </Box>
        )}

        {!loading && error && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/account-settings')}
            >
              Back to Account Settings
            </Button>
          </>
        )}

        {!loading && success && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your account has been successfully deleted. You will be logged out
              shortly.
            </Alert>
            <Typography variant="body2" color="text.secondary" align="center">
              Redirecting to login page...
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyAccountDeletion;
